<script setup lang="ts">
import { useGameStore } from '@/stores/gameStore.ts'
import { useAssetStore } from '@/stores/assetStore.ts'
import { computed } from 'vue'
import Backend from '@/models/backend.ts'
import { CDN } from '@/models/cdn.ts'
import LargeWideModel from '@/components/Interface/Modal/ModelSizes/LargeWideModel.vue'
import StandardCard from '@/components/Elements/Cards/StandardCard.vue'
import NamedNumbers from '@/components/Elements/Generic/NamedNumbers.vue'
import GenericIconWithNumber from '@/components/Elements/Images/Icons/GenericIconWithNumber.vue'
import ModalTechBuilding from '@/components/Interface/Modal/Modals/ModalTech/ModalTechBuilding.vue'
import ModalTechShipComponent from '@/components/Interface/Modal/Modals/ModalTech/ModalTechShipComponent.vue'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import SimpleProgressBar from '@/components/Elements/ProgressBar/SimpleProgressBar.vue'
import DeleteButton from '@/components/Elements/Buttons/DeleteButton.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

/**
 * Stores / models
 */
const gameStore = useGameStore()
const assetStore = useAssetStore()

/**
 * Computed
 */
const techData = computed(() => assetStore.findTechById(gameStore.modalData.techId)!)

const isPrototype = computed(() => {
  return gameStore.playerPrototypes.has(gameStore.modalData.techId)
})

const effectiveCost = computed(() => {
  if (isPrototype.value && gameStore.player?.effectiveAllianceResearchCostMultiplier) {
    return Math.ceil(techData.value.cost * gameStore.player.effectiveAllianceResearchCostMultiplier)
  }
  return techData.value.cost
})


const buildings = computed(() => {
  const buildings = []
  for (const benefit of techData.value.provides) {
    if (benefit.type === 'buildings') {
      buildings.push(benefit.code)
    }
  }
  return buildings
})

const shipComponents = computed(() => {
  const shipComponents = []
  for (const benefit of techData.value.provides) {
    if (benefit.type === 'ship_components') {
      shipComponents.push(benefit.code)
    }
  }
  return shipComponents
})

const techPercentage = computed(() => {
  if (gameStore.player?.currentTechId != techData.value.id) {
    return -1
  }
  const percentage = Math.floor(
    (Number(gameStore.player?.sciencePoints) / effectiveCost.value) * 100
  )
  if (percentage > 100) {
    return 100
  }
  return percentage
})

const isInQueue = computed(() => {
  return gameStore.playerTechQueue.queue?.includes(gameStore.modalData.techId) || false
})


const isResearched = computed(() => {
  return gameStore.playerTechs.has(gameStore.modalData.techId)
})

const techStatus = computed(() => {
  if (gameStore.player?.currentTechId === gameStore.modalData.techId) {
    return 'researching'
  }
  if (isResearched.value) {
    return 'researched'
  }
  if (gameStore.player?.targetTechId === gameStore.modalData.techId) {
    return 'target'
  }

  // Check if it's a prerequisite
  const targetTech = assetStore.findTechById(gameStore.player?.targetTechId || 0)
  if (targetTech && targetTech.requirementTree) {
    for (const requirementTechId of targetTech.requirementTree) {
      if (requirementTechId === gameStore.modalData.techId) {
        return 'prerequisite'
      }
    }
  }

  if (isInQueue.value) {
    return 'queued'
  }

  return 'available'
})

const showResearchButton = computed(() => {
  // Don't show if already researched
  if (isResearched.value) {
    return false
  }

  // Don't show if currently being researched or is the target
  if (
    gameStore.player?.currentTechId === gameStore.modalData.techId ||
    gameStore.player?.targetTechId === gameStore.modalData.techId
  ) {
    return false
  }

  // Show for requisites, queued, and available techs
  return true
})

const showQueueButton = computed(() => {
  // Don't show if already researched
  if (isResearched.value) {
    return false
  }

  // Don't show if it's the current target
  if (gameStore.player?.targetTechId === gameStore.modalData.techId) {
    return false
  }

  // Don't show if already in queue
  if (isInQueue.value) {
    return false
  }

  // Show if it's a requisite or if there's a current target
  return techStatus.value === 'prerequisite' || gameStore.player?.targetTechId !== 0
})

const showDequeueButton = computed(() => {
  return isInQueue.value
})

/**
 * Functions
 */
const setTech = () => {
  const backend = new Backend()
  backend.setTechTarget(gameStore.modalData.techId).then(() => {
    gameStore.modalData = []
    gameStore.modalType = ''
  })
  gameStore.setTinySuccessToastMessage(t('research.actions.techSetAsTarget'))
}

const dequeueTech = () => {
  const backend = new Backend()
  const currentQueue = [...(gameStore.playerTechQueue.queue || [])]
  const index = currentQueue.indexOf(gameStore.modalData.techId)
  if (index > -1) {
    currentQueue.splice(index, 1)
    backend.updateTechQueue(currentQueue).then(() => {
      gameStore.setTinySuccessToastMessage(t('research.queue.removed'))
      gameStore.modalData = []
      gameStore.modalType = ''
    })
  }
}

const queueTech = () => {
  const backend = new Backend()
  const currentQueue = [...(gameStore.playerTechQueue.queue || [])]
  if (!currentQueue.includes(gameStore.modalData.techId)) {
    currentQueue.push(gameStore.modalData.techId)
    backend.updateTechQueue(currentQueue).then(() => {
      gameStore.setTinySuccessToastMessage(t('research.queue.added'))
      gameStore.modalData = []
      gameStore.modalType = ''
    })
  }
}
</script>

<template>
  <LargeWideModel :title="t(`research.techs.${techData.code}.name`)">
    <div class="p-2 lg:flex">
      <div class="w-full lg:w-1/2">
        <div class="flex flex-col md:flex-row">
          <img
            :src="CDN.technology(256, techData.code)"
            alt="Technology"
            class="shrink-0 rounded w-auto h-auto md:w-64 md:h-64 object-cover"
          />
          <div class="md:ps-2 pt-2 md:pt-0 w-full">
            <StandardCard :title="t(`research.techs.${techData.code}.name`)" top-padding="0">
              <p>{{ t(`research.techs.${techData.code}.description`) }}</p>
              <div class="pt-2 flex justify-between items-center">
                <NamedNumbers :text="t('research.scienceCost')"
                  ><GenericIconWithNumber :amount="effectiveCost" type="science"
                /></NamedNumbers>
                <div v-if="techPercentage !== -1" class="w-1/2">
                  <SimpleProgressBar
                    :percentage="techPercentage"
                    color="blue"
                    :text="techPercentage + '%'"
                    :height="5"
                    :subtext="`${gameStore.player!.sciencePoints} / ${effectiveCost}`"
                  />
                </div>
                <div v-else>
                  <ActionButton v-if="showResearchButton" @click="setTech"
                    >{{ t('research.actions.startResearch') }}</ActionButton
                  >
                  <DeleteButton v-if="showDequeueButton" @click="dequeueTech" class="ml-2"
                    >{{ t('research.queue.dequeue') }}</DeleteButton
                  >
                  <ActionButton v-if="showQueueButton" @click="queueTech" class="ml-2"
                    >{{ t('research.queue.addToQueue') }}</ActionButton
                  >
                </div>
              </div>
            </StandardCard>
          </div>
        </div>
      </div>
      <div class="w-full lg:w-1/2 ps-0 lg:ps-2 pt-2 lg:pt-0">
        <template v-for="buildingCode in buildings" :key="buildingCode">
          <ModalTechBuilding :buildingCode="buildingCode" />
        </template>
        <template v-for="shipComponentCode in shipComponents" :key="shipComponentCode">
          <ModalTechShipComponent :shipComponentCode="shipComponentCode" />
        </template>
      </div>
    </div>
  </LargeWideModel>
</template>

<style scoped lang="scss">
.main-image {
  width: 15rem;
  height: 15rem;
}
</style>
